import { useNavigate } from "react-router-dom";

const UPDATED = "August 2026";

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="text-2xl text-white leading-tight">{title}</h2>
      <div className="space-y-3 text-slate-400 leading-relaxed">{children}</div>
    </section>
  );
}

export default function DataDeletion() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#07090E] text-slate-200">
      <nav className="sticky top-0 z-40 bg-[#07090E]/85 backdrop-blur-xl border-b border-[#1E273D]">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="data-deletion-home">
            <img src="/openword-logo.png" alt="OpenWord AI" className="w-8 h-8" />
            <span className="text-xl text-white">OpenWord AI</span>
          </button>
          <button onClick={() => navigate("/")} className="text-sm text-slate-400 hover:text-[#00E5FF] transition-colors">
            Back to home
          </button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-16 space-y-12" data-testid="data-deletion-page">
        <header className="space-y-3">
          <div className="text-xs font-medium uppercase tracking-[0.2em] text-[#00E5FF]">Data Deletion</div>
          <h1 className="text-4xl sm:text-5xl text-white leading-tight">Deleting your data.</h1>
          <p className="text-lg text-slate-400 leading-relaxed">
            You can ask us to delete your OpenWord AI account, your study content, and any Facebook Page data we
            hold, at any time. Here's exactly how.
          </p>
          <p className="text-sm text-slate-500">Last updated: {UPDATED}</p>
        </header>

        <Section title="Delete your OpenWord AI account and study content">
          <p>
            To delete your account and everything tied to it — your email, name, study content, notes, reflections,
            saved prayers, and virtual study companion conversations — send a request from our{" "}
            <a href="/support" className="text-[#00E5FF] hover:underline">support form</a> using the email address on
            your account, or email us and ask us to delete your account. We will remove your personal information and
            study content within 30 days, except anything we are legally required to retain.
          </p>
        </Section>

        <Section title="Delete Facebook Page data">
          <p>
            If you connected a Facebook Page so our assistant could help with comments, you can remove that data in
            either of these ways:
          </p>
          <ul className="space-y-3">
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">In OpenWord AI:</strong> open Admin → FB Comments → <strong className="text-slate-200">Disconnect</strong>. This deletes the stored Page access token and stops all further access immediately.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span><strong className="text-slate-200">In Facebook:</strong> go to Facebook → <strong className="text-slate-200">Settings &amp; Privacy → Settings → Business Integrations</strong>, find <strong className="text-slate-200">OpenWord AI</strong>, and click <strong className="text-slate-200">Remove</strong>. This revokes our access to your Page.</span></li>
          </ul>
          <p>
            When you disconnect, we delete the Page access token and the cached comment records associated with your
            Page from our systems. We never store your private Facebook messages or personal profile data.
          </p>
        </Section>

        <Section title="What we keep">
          <p>
            After a deletion request we remove your personal data and content. We may retain a minimal record where
            required for legal, accounting, or security reasons (for example, a record that a transaction occurred),
            and Stripe retains payment records under its own policies.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions or requests? Reach us through our{" "}
            <a href="/support" className="text-[#00E5FF] hover:underline">support form</a> and we'll take care of it.
          </p>
          <p className="text-sm text-slate-500">Powered by OpenWord AI.</p>
        </Section>
      </main>

      <footer className="border-t border-[#1E273D] bg-[#0E131F]">
        <div className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between text-sm text-slate-500">
          <button onClick={() => navigate("/")} className="text-lg text-slate-200 hover:text-[#00E5FF]">OpenWord AI</button>
          <span className="text-xs text-slate-500">© 2026 Powered by OpenWord AI</span>
        </div>
      </footer>
    </div>
  );
}
